import './Page.css';
import './AboutPage.css';
import { useQuestionCounts, formatCount } from '../hooks/useQuestionCounts';

const weServe = [
  'Students new to the U.S. school system',
  'Middle schoolers preparing for competitive high school admissions',
  'High schoolers navigating college applications and essays',
  'Students aiming to boost SAT/ACT test scores',
  'STEM-focused students needing stronger ELA skills',
  'Busy students balancing academics with extracurriculars',
  'Families looking for flexible, personalized tutoring',
];

const selectionSteps = [
  { step: '01', label: 'Academic Vetting', desc: 'Top scores, degrees, and proven experience.' },
  { step: '02', label: 'Teaching Sample', desc: 'Demonstrated clarity and student engagement.' },
  { step: '03', label: 'Student Adaptability', desc: 'Ability to personalize and adjust sessions.' },
  { step: '04', label: 'Ongoing Training', desc: 'Strategy updates, AI tools, and diagnostics.' },
];

export default function AboutPage() {
  const counts = useQuestionCounts();

  const stats = [
    { value: formatCount(counts.grand), label: 'Practice Questions' },
    { value: '150+', label: 'Free Consultations' },
    { value: '140+', label: 'Avg. SAT Score Lift' },
    { value: '6–12', label: 'Grades Served' },
  ];

  const offerings = [
    { icon: '📚', text: `${formatCount(counts.grand)} Question Bank for SAT & ACT Prep – Plus Expert Tutor Support.` },
    { icon: '✍️', text: 'ELA Mastery – Grammar, Reading Comprehension & Academic Writing.' },
    { icon: '🎯', text: 'Subject-Specific Tutoring – Build Fluency & Boost Performance.' },
    { icon: '🏛️', text: 'College Admissions Coaching – Personal Statements & Essay Planning.' },
    { icon: '🗺️', text: 'Comprehensive 4-Year High School Planning.' },
    { icon: '🎓', text: 'Application & Scholarship Guidance.' },
  ];

  return (
    <div className="page-wrap">

      {/* Hero */}
      <div className="navy-hero">
        <div className="container">
          <p className="about-eyebrow-hero">Our Story</p>
          <h1>About Scholar Sphere</h1>
          <p className="page-hero-sub">Personalized Tutoring & College Coaching for Grades 6–12</p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="about-stats-bar">
        <div className="container">
          <div className="about-stats-grid">
            {stats.map(s => (
              <div className="about-stat" key={s.label}>
                <span className="about-stat-value">{s.value}</span>
                <span className="about-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* Founding Story */}
        <div className="about-story-block" style ={{ textAlign: 'left', marginTop: 32 }}>
          <div className="about-story-text">
            <span className="about-badge">Our Mission</span>
            <h2 className="about-section-title">Born from Inspiration.<br />Built for Excellence.</h2>
            <p className="page-p">
              Scholar Sphere was founded on a simple belief: <strong>every student deserves a personalized path to success.</strong>
            </p>
            <p className="page-p">
              Created by Nidhi and Anshu — parents and passionate advocates for better learning — Scholar Sphere offers more than traditional tutoring. We deliver custom academic instruction, expert test prep, and college admissions guidance to help students grow with purpose and confidence.
            </p>
            <p className="page-p">
              By combining live, expert-led instruction with smart diagnostic tools and AI-informed insights, we create programs that adapt to each learner's strengths and goals — turning ambition into achievement.
            </p>
          </div>
          <div className="about-story-founders">
            <div className="about-founder-card">
              <div className="about-founder-avatar">N</div>
              <div>
                <div className="about-founder-name">Nidhi</div>
                <div className="about-founder-role">Co-CEO & Program Director</div>
              </div>
            </div>
            <div className="about-founder-card">
              <div className="about-founder-avatar">A</div>
              <div>
                <div className="about-founder-name">Anshu</div>
                <div className="about-founder-role">Co-CEO & Technology Lead</div>
              </div>
            </div>
            <p className="about-founder-note">
              Together, Nidhi and Anshu blend educational insight with cutting-edge technology — real-time progress tracking, data-informed study planning, and family-centered communication.
            </p>
          </div>
        </div>

        {/* What We Offer */}
        <div className="about-offer-block">
          <span className="about-badge">What We Offer</span>
          <h2 className="about-section-title">A Complete Academic Support System</h2>
          <p className="page-p">We combine a comprehensive self-paced question bank with expert live instruction — meeting students wherever they are and helping them go further.</p>
          <div className="about-offer-grid">
            {offerings.map(o => (
              <div className="about-offer-card" key={o.text}>
                <span className="about-offer-icon">{o.icon}</span>
                <span>{o.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Who We Serve */}
        <div className="about-serve-block">
          <div className="about-serve-left">
            <span className="about-badge">Who We Serve</span>
            <h2 className="about-section-title">Built for Every Student's Journey</h2>
            <p className="page-p">
              Every Scholar Sphere journey starts with a free personal consultation. Co-founder Nidhi has completed <strong>150+ consultations</strong> to help families gain clarity, navigate the U.S. school system, and map out a high school success plan.
            </p>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
          </div>
          <ul className="about-serve-list">
            {weServe.map(w => (
              <li key={w}>
                <span className="about-serve-check">✓</span>
                {w}
              </li>
            ))}
          </ul>
        </div>

        {/* Tutors */}
        <div className="about-tutors-block">
          <div className="about-tutors-header">
            <span className="about-badge">Our Team</span>
            <h2 className="about-section-title">How We Select Our Tutors</h2>
            <p className="page-p">Every tutor is certified, experienced, and committed to student success — with expertise in SAT, ACT, AP, and ELA instruction.</p>
          </div>
          <div className="about-steps-grid">
            {selectionSteps.map(s => (
              <div className="about-step-card" key={s.step}>
                <div className="about-step-num">{s.step}</div>
                <div className="about-step-label">{s.label}</div>
                <div className="about-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <a href="/tutors" className="btn-navy">Meet Our Tutors</a>
          </div>
        </div>

        {/* Advisors */}
        <div className="about-advisors-block">
          <span className="about-badge">Our Advisors</span>
          <h2 className="about-section-title">Inspired by Independent Advisors</h2>
          <p className="page-p">
            Scholar Sphere is shaped by the real-world insights of advisors — successful professionals, CEOs, authors, and parents who motivate students and families alike.
          </p>
          <div className="about-advisor-pills">
            <span>Parents of Ivy League graduates</span>
            <span>First-generation college grads</span>
            <span>Entrepreneurs & educators</span>
            <span>Purpose-driven leaders</span>
          </div>
          <p className="page-p" style={{ marginTop: 16 }}>
            Their contributions — through blog posts, interviews, and community insights — make Scholar Sphere more than a tutoring service. It's a network of mentorship, wisdom, and inspiration.
          </p>
        </div>

        {/* CTA */}
        <div className="cta-strip" style={{marginBottom: 32}} >
          <h3>Ready to Get Started?</h3>
          <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
        </div>

      </div>
    </div>
  );
}
